import React, { useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import { Description, PictureAsPdf, TableView } from '@mui/icons-material';
import PillTabs from 'components/shared/tabs/PillTabs';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import OrgExpandableRow from 'components/org-client/OrgExpandableRow';
import ProtectedBadge from 'components/org-client/ProtectedBadge';
import PrivacyNote from 'components/org-client/PrivacyNote';
import useOrgPageTitle from 'components/org-client/useOrgPageTitle';
import useLogger from 'hooks/useLogger';
import { exportWellbeingToExcel } from 'components/admin-organizations/wellbeingExport';
import { bgOf, toneOf } from 'components/admin-organizations/wellbeingTones';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  MY_ORGANIZATION,
  MY_ORGANIZATION_HEATMAP,
  MY_ORGANIZATION_REPORTS,
  MY_ORGANIZATION_REPORT_URL,
  MY_ORGANIZATION_WELLBEING
} from 'api/graphql/orgClient';

const WINDOWS = [4, 8, 12];

const formatDate = (iso) => {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '--';
  }
};

/**
 * Reportes de bienestar: descarga del informe vigente (PDF firmado por el backend o Excel
 * generado en el navegador) y el histórico de los que ya se emitieron.
 */
function OrgClientReports() {
  const { t } = useTranslation(['orgClient']);
  const dispatch = useDispatch();
  const { logError } = useLogger();
  const pageTitle = t('menu.reports', { defaultValue: 'Reportes' });
  useOrgPageTitle(pageTitle);

  const [windowIndex, setWindowIndex] = useState(0);
  const weeks = WINDOWS[windowIndex];

  const { data: orgData } = useQuery(MY_ORGANIZATION, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientReports', queryError)
  });
  const { data: summaryData } = useQuery(MY_ORGANIZATION_WELLBEING, {
    variables: { weeks },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientReports', queryError)
  });
  const { data: heatmapData } = useQuery(MY_ORGANIZATION_HEATMAP, {
    variables: { weeks },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientReports', queryError)
  });
  const { data: reportsData, loading, error } = useQuery(MY_ORGANIZATION_REPORTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientReports', queryError)
  });
  const [fetchReportUrl, { loading: loadingPdf }] = useLazyQuery(MY_ORGANIZATION_REPORT_URL, {
    fetchPolicy: 'network-only'
  });

  const organization = orgData?.myOrganization || null;
  const summary = summaryData?.myOrganizationWellbeing || null;
  const heatmap = useMemo(() => heatmapData?.myOrganizationHeatmap || [], [heatmapData]);
  const reports = useMemo(() => reportsData?.myOrganizationReports || [], [reportsData]);

  // La hoja de participación del Excel se arma con la tendencia del resumen: es la misma
  // serie semanal (respuestas sobre miembros) y evita una consulta extra solo para exportar.
  const participation = useMemo(
    () =>
      (summary?.trend || []).map((point) => ({
        week_key: point.week_key,
        respondents: point.respondents,
        members: summary?.members ?? 0,
        percent: summary?.members ? Math.round((point.respondents / summary.members) * 100) : 0
      })),
    [summary]
  );

  const snack = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `org-reports-${variant}`, variant, message })));

  const handlePdf = async () => {
    try {
      const { data } = await fetchReportUrl({ variables: { weeks } });
      const url = data?.myOrganizationReportUrl;
      if (!url) throw new Error('empty report url');
      window.open(url, '_blank', 'noopener');
    } catch (downloadError) {
      logError('OrgClientReports.pdf', downloadError);
      snack('error', t('reports.pdfError', { defaultValue: 'No pudimos generar el PDF. Intenta de nuevo.' }));
    }
  };

  const handleExcel = () => {
    try {
      exportWellbeingToExcel({
        organizationName: organization?.name,
        summary,
        heatmap,
        participation
      });
    } catch (exportError) {
      logError('OrgClientReports.excel', exportError);
      snack('error', t('reports.excelError', { defaultValue: 'No pudimos generar el Excel. Intenta de nuevo.' }));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <PremiumCard sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {t('reports.downloadTitle', { defaultValue: 'Descarga el informe de tu empresa' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {t('reports.downloadBody', {
                defaultValue:
                  'El informe trae los mismos agregados anónimos que ves en pantalla. Los grupos protegidos salen marcados como protegidos, nunca con un número inventado.'
              })}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <PillTabs
                value={windowIndex}
                onChange={(event, next) => setWindowIndex(next)}
                ariaLabel={t('filters.windowAria', { defaultValue: 'Ventana de tiempo' })}
                tabs={WINDOWS.map((option) => ({
                  key: `w-${option}`,
                  label: t('filters.weeks', { count: option, defaultValue: '{{count}} semanas' })
                }))}
              />
            </Box>
          </Grid>
          <Grid xs={12} md={6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent={{ md: 'flex-end' }}>
              <Button
                onClick={handlePdf}
                disabled={loadingPdf}
                startIcon={<PictureAsPdf />}
                sx={{ ...premiumButtonSx, px: 3 }}>
                {t('reports.pdf', { defaultValue: 'Descargar PDF' })}
              </Button>
              <Button
                onClick={handleExcel}
                disabled={!summary}
                variant="outlined"
                color="inherit"
                startIcon={<TableView />}
                sx={{
                  px: 3,
                  py: 1.4,
                  borderRadius: 2.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  color: 'text.primary',
                  borderColor: 'hairline.strong',
                  transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)',
                  '&:active': { transform: 'scale(0.98)' }
                }}>
                {t('reports.excel', { defaultValue: 'Exportar a Excel' })}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </PremiumCard>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
        {t('reports.historyTitle', { defaultValue: 'Informes emitidos' })}
      </Typography>

      {error && reports.length === 0 && (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3, mb: 2.5 }}>
          {t('errors.reports', { defaultValue: 'No pudimos cargar el histórico. Intenta de nuevo en un momento.' })}
        </Alert>
      )}

      {loading && reports.length === 0 ? (
        <Stack spacing={1}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} variant="rounded" height={68} sx={{ borderRadius: '14px' }} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={1}>
          {reports.length === 0 ? (
            <Box
              sx={{
                p: 4,
                borderRadius: 3,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'hairline.strong',
                backgroundColor: 'surface.overlay'
              }}>
              <Description sx={{ color: 'text.disabled', fontSize: 32 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1 }}>
                {t('reports.emptyTitle', { defaultValue: 'Todavía no hay informes emitidos' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 460, mx: 'auto' }}>
                {t('reports.emptyBody', {
                  defaultValue:
                    'Aquí se van guardando los informes a medida que se generan. Mientras tanto puedes descargar el informe del momento con los botones de arriba.'
                })}
              </Typography>
            </Box>
          ) : (
            reports.map((report) => (
              <OrgExpandableRow
                key={report.id}
                title={t(`reports.trigger.${report.trigger}`, { defaultValue: report.trigger })}
                subtitle={t('reports.rowMeta', {
                  date: formatDate(report.created_at),
                  weeks: report.weeks,
                  defaultValue: '{{date}} · ventana de {{weeks}} semanas'
                })}
                trailing={
                  report.was_protected ? (
                    <ProtectedBadge compact />
                  ) : (
                    <Chip
                      size="small"
                      label={report.score ?? '--'}
                      sx={{
                        flex: 'none',
                        fontWeight: 800,
                        fontVariantNumeric: 'tabular-nums',
                        color: toneOf(report.score),
                        backgroundColor: bgOf(report.score)
                      }}
                    />
                  )
                }>
                <Stack spacing={1} sx={{ pt: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('reports.rowDetail', {
                      respondents: report.respondents,
                      members: report.members,
                      percent: Math.round(report.participation_percent || 0),
                      defaultValue: 'Respondieron {{respondents}} de {{members}} personas ({{percent}}%).'
                    })}
                  </Typography>
                  {report.week_key && (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      {t('reports.weekKey', { week: report.week_key, defaultValue: 'Semana {{week}}' })}
                    </Typography>
                  )}
                  {report.was_protected && <ProtectedBadge />}
                </Stack>
              </OrgExpandableRow>
            ))
          )}
        </Stack>
      )}

      <Box sx={{ mt: 3 }}>
        <PrivacyNote />
      </Box>
    </Box>
  );
}

export default OrgClientReports;
